import React, { useState, useContext, useEffect, useRef } from "react";
import { Link } from "@inertiajs/react";
import { Transition } from "@headlessui/react";
import { cn } from "@/util";

type InertiaLinkProps = React.ComponentProps<typeof Link>;

/**
 * The verbs `Dropdown.Link` accepts, which are Inertia's own.
 *
 * Exported because a caller that stores one in a prop of its own — `DropdownAvatar` holds a
 * `logoutRouteMethod`, and `CentralAdminWrapper` passes `"get"` for it — has to name the same type
 * or it lands back at `string`. Re-declaring the union at each such caller is how one of them ends
 * up admitting a verb Inertia does not.
 */
export type DropdownLinkMethod = InertiaLinkProps["method"];

const DropDownContext = React.createContext({
    open: false,
    setOpen: (open: boolean) => {},
    toggleOpen: () => {},
});

/**
 * The one dropdown primitive for menus that hang off the top bar — the account menus
 * (`DropdownAvatar`, `UserNavDropdown`) and the navigation menu in `Authenticated`.
 *
 * Three ergonomics were added 27 Aug, and the third is the one people actually notice:
 *
 *  - **Escape closes it.** There was no keyboard way out at all.
 *  - **Focus returns to the trigger** on close, rather than being dropped on `<body>` — which is
 *    what makes a keyboard user restart their traversal from the top of the page.
 *  - **Outside clicks LAND.** Closing was implemented as a `fixed inset-0` overlay that swallowed
 *    the click that dismissed it, so dismissing the menu and pressing the thing you were aiming at
 *    were always two clicks. A `pointerdown` listener outside the wrapper closes without
 *    intercepting, so the second click is not needed.
 */
const DropdownComponent = ({ children }) => {
    const [open, setOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const wasOpen = useRef(false);

    const toggleOpen = () => {
        setOpen((previousState) => !previousState);
    };

    useEffect(() => {
        if (!open) {
            // Closing: hand focus back to whatever inside the trigger opened this. Guarded on the
            // previous state so a dropdown that has never been opened does not steal focus on mount.
            if (wasOpen.current) {
                wrapperRef.current
                    ?.querySelector<HTMLElement>(
                        'button, [href], [tabindex]:not([tabindex="-1"])',
                    )
                    ?.focus();
            }
            wasOpen.current = false;
            return;
        }

        wasOpen.current = true;

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setOpen(false);
        };

        const onPointerDown = (event: Event) => {
            if (!wrapperRef.current?.contains(event.target as Node)) {
                setOpen(false);
            }
        };

        document.addEventListener("keydown", onKeyDown);
        document.addEventListener("pointerdown", onPointerDown);

        return () => {
            document.removeEventListener("keydown", onKeyDown);
            document.removeEventListener("pointerdown", onPointerDown);
        };
    }, [open]);

    return (
        <DropDownContext.Provider value={{ open, setOpen, toggleOpen }}>
            <div ref={wrapperRef} className="relative z-[9999]">
                {children}
            </div>
        </DropDownContext.Provider>
    );
};

const Trigger = ({ children }) => {
    const { toggleOpen } = useContext(DropDownContext);

    return <div onClick={toggleOpen}>{children}</div>;
};

const Content = ({
    align = "right",
    width = "48",
    contentClasses = "py-1 bg-white text-slate-900 dark:bg-slate-800 dark:text-slate-100",
    children,
}) => {
    const { open, setOpen } = useContext(DropDownContext);

    let alignmentClasses = "origin-top";

    if (align === "left") {
        alignmentClasses = "origin-top-left left-0";
    } else if (align === "right") {
        alignmentClasses = "origin-top-right right-0";
    }

    let widthClasses = "";

    if (width === "48") {
        widthClasses = "w-48";
    } else if (width === "56") {
        // For a menu carrying a header — a name over an email needs more room than a column of
        // one-word entries, and 48 wraps most addresses onto a second line.
        widthClasses = "w-56";
    }

    return (
        <>
            <Transition
                as={"span"}
                show={open}
                enter="transition ease-out duration-200"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100 z-50"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                {open && (
                    <div
                        className={`absolute z-50 mt-2 rounded-md shadow-lg ${alignmentClasses} ${widthClasses}`}
                        onClick={() => setOpen(false)}
                    >
                        <div
                            className={
                                "rounded-md ring-1 ring-black ring-opacity-5 dark:ring-white/10 " +
                                contentClasses
                            }
                        >
                            {children}
                        </div>
                    </div>
                )}
            </Transition>
        </>
    );
};

/**
 * The look of one entry, shared by `Dropdown.Link` and `Dropdown.Button`.
 *
 * A constant rather than a copy in each: they differ only in what a click DOES, and an entry that
 * navigates sitting beside one that acts should be indistinguishable until you press it. Two copies
 * of this string is how one of them ends up a pixel off.
 *
 * `flex` rather than `block`, so an entry can lead with an icon and have its label line up with the
 * labels above and below it. A text-only entry is unaffected: one child at flex-start sits exactly
 * where `text-left` put it, which is why every existing call site could stay as it was.
 */
const entryClasses = `flex w-full items-center gap-2.5 border-l-2 border-transparent px-4 py-2 text-left text-sm
    leading-5 text-gray-700 transition duration-150 ease-in-out hover:bg-gray-100 focus:bg-gray-100
    focus:outline-none dark:text-slate-200 dark:hover:bg-slate-700 dark:focus:bg-slate-700`;

/**
 * The current page, as a menu row.
 *
 * **The leading border is on EVERY entry**, transparent until it is not — the same construction
 * `NavLink` uses for the bar's bottom border, and the reason neither has to compensate its padding.
 * An active-only `border-l-2` would push the label 2px right of the labels above it, which reads as
 * a wobble rather than as emphasis. Under `box-sizing: border-box` every row's text starts at the
 * same offset whatever the border's colour is.
 *
 * Indigo because that is the colour the bar already spends on this exact fact. The bar's mark is a
 * bottom border under a horizontal item; rotated onto the leading edge of a vertical one, it is the
 * same mark rather than a second vocabulary.
 */
const entryActiveClasses = `border-indigo-400 bg-gray-50 font-medium text-gray-900 dark:border-indigo-500
    dark:bg-slate-700/40 dark:text-slate-100`;

const DropdownLink = ({
    href,
    method,
    as = "a",
    active = false,
    children,
}: {
    href: string;
    /**
     * Derived from `Link`'s own props rather than restated as `string`.
     *
     * This component was untyped until the `active` prop arrived, so `method` and `as` reached
     * Inertia as implicit `any` and nothing checked them. Annotating them `string` — the obvious
     * move — is what `tsc` rejected: `Method` is a union of verbs and `as` is an `ElementType`, and
     * widening both to `string` at this seam would have been loosening a shared component's props
     * to make one caller compile.
     */
    method?: InertiaLinkProps["method"];
    as?: InertiaLinkProps["as"];
    /**
     * This entry is the page you are on. Only the navigation menu passes it — an account menu is a
     * list of actions, and marking one of them "current" would be a claim about the wrong thing.
     */
    active?: boolean;
    children?: React.ReactNode;
}) => {
    return (
        <Link
            href={href}
            method={method}
            as={as}
            aria-current={active ? "page" : undefined}
            className={cn(entryClasses, active && entryActiveClasses)}
        >
            {children}
        </Link>
    );
};

/**
 * An entry that DOES something rather than going somewhere — the install prompt is the first, since
 * opening the browser's install dialog is a call, not a destination.
 *
 * Not expressible as `Dropdown.Link`: that is an Inertia `Link`, so it needs a URL even with
 * `as="button"`, and giving it a fake one to hang an `onClick` on would put a real destination in
 * the DOM for middle-click and "open in new tab" to find.
 */
const DropdownButton = ({
    onClick,
    children,
}: {
    onClick: () => void;
    children?: React.ReactNode;
}) => (
    <button type="button" onClick={onClick} className={entryClasses}>
        {children}
    </button>
);

/**
 * Who this menu belongs to — a name over an email, at the top.
 *
 * Not a link and not focusable: it is a read-back, and putting it in the tab order would make every
 * keyboard user pass through it to reach the first thing they can actually do.
 */
const Header = ({ name, email }: { name?: string; email?: string }) => {
    if (!name && !email) return null;

    return (
        <div className="px-4 pb-1.5 pt-1">
            {!!name && (
                <div className="truncate text-sm font-semibold text-gray-900 dark:text-slate-100">
                    {name}
                </div>
            )}
            {!!email && (
                <div className="truncate text-xs text-gray-500 dark:text-slate-400">
                    {email}
                </div>
            )}
        </div>
    );
};

/**
 * A rule between groups of entries. `aria-hidden` — it is a visual grouping, and announcing
 * "separator" between every pair of items is noise rather than structure.
 */
const Divider = () => (
    <div
        aria-hidden="true"
        className="my-1 border-t border-gray-100 dark:border-slate-700"
    />
);

/**
 * A group of entries with a rule under it, which **disappears when nothing renders into it**.
 *
 * `empty:hidden` rather than a `{children && …}` guard, and the difference is the whole point: a slot
 * is filled with self-guarding components, so `children` is a truthy React element even on the render
 * where that component returns `null`. A caller passing `<LeavePracticeItem />` to every viewer gets a
 * real entry for a Sapien Super Admin and nothing for a customer — from one identical, always-truthy
 * prop. React cannot tell those apart before rendering; CSS can tell them apart after, because only
 * one of them puts a child in the DOM.
 *
 * (This shipped wrong first: `{!!children && <Divider />}` drew a second rule under an empty group in
 * every customer's menu, which is exactly the case the guard was written to prevent.)
 */
const Group = ({ children }: { children?: React.ReactNode }) => (
    <div className="mb-1 border-b border-gray-100 pb-1 empty:hidden dark:border-slate-700">
        {children}
    </div>
);

DropdownComponent.Trigger = Trigger;
DropdownComponent.Content = Content;
DropdownComponent.Link = DropdownLink;
DropdownComponent.Button = DropdownButton;
DropdownComponent.Header = Header;
DropdownComponent.Divider = Divider;
DropdownComponent.Group = Group;

export const Dropdown = DropdownComponent;
